import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import {
  X, Save, RotateCcw, FileText, TrendingUp,
  Search, CheckSquare, AlertCircle, User, Package,
  Plus, Trash2, Printer
} from 'lucide-react';
import apiClient from '../services/api';

const VIOLET     = '#667eea';
const VIOLET_MID = '#7a5cbf';
const VIOLET_DK  = '#764ba2';

/* ── InvoicePrint: printable modal — all inline styles kept for window.open context ── */
const InvoicePrint = ({ invoice, items, onClose }) => {
  const subTotal   = items.reduce((s, r) => s + parseFloat(r.cpd_Amount || r.UnitPrice || 0), 0);
  const taxTotal   = items.reduce((s, r) => s + parseFloat(r.TaxAmt || 0), 0);
  const grandTotal = subTotal + taxTotal;

  const handlePrint = () => {
    const printContents = document.getElementById('invoice-print-area').innerHTML;
    const printWindow   = window.open('', '_blank', 'width=900,height=700');
    printWindow.document.write(`<!DOCTYPE html><html><head><title>Invoice - ${invoice.InvoiceNo}</title><style>body{font-family:Arial,sans-serif;margin:0;padding:20px;color:#1a1a1a;}table{border-collapse:collapse;width:100%;}th{background:#1e3a5f;color:#fff;padding:10px 12px;text-align:left;font-size:12px;}td{padding:10px 12px;border-bottom:1px solid #e2e8f0;font-size:12px;}.text-right{text-align:right;}@media print{@page{margin:15mm;}}</style></head><body>${printContents}<script>window.onload=function(){window.print();window.close();}</script></body></html>`);
    printWindow.document.close();
  };

  return ReactDOM.createPortal(
    <div style={{ position:'fixed',inset:0,zIndex:99999,background:'rgba(0,0,0,0.7)',display:'flex',alignItems:'flex-start',justifyContent:'center',overflowY:'auto',padding:'20px 16px' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background:'#fff',borderRadius:12,width:'100%',maxWidth:820,boxShadow:'0 20px 60px rgba(0,0,0,0.4)',overflow:'hidden' }}
        onClick={(e) => e.stopPropagation()}>
        <div style={{ background:'#1e3a5f',padding:'14px 20px',display:'flex',alignItems:'center',justifyContent:'space-between' }}>
          <div style={{ display:'flex',alignItems:'center',gap:10 }}>
            <FileText size={22} color="#fff" />
            <span style={{ color:'#fff',fontWeight:700,fontSize:'1rem' }}>Invoice — {invoice.InvoiceNo}</span>
          </div>
          <div style={{ display:'flex',gap:10 }}>
            <button onClick={handlePrint} style={{ background:'#fff',color:'#1e3a5f',border:'none',borderRadius:6,padding:'7px 16px',fontWeight:700,cursor:'pointer',fontSize:'0.85rem',display:'flex',alignItems:'center',gap:6 }}>
              <Printer size={15} /> Print / Save PDF
            </button>
            <button onClick={onClose} style={{ background:'rgba(255,255,255,0.2)',border:'none',borderRadius:6,padding:'7px 10px',cursor:'pointer',color:'#fff',display:'flex' }}>
              <X size={18} color="#fff" />
            </button>
          </div>
        </div>
        <div id="invoice-print-area" style={{ padding:'36px 40px',fontFamily:'Arial,sans-serif',fontSize:13,color:'#1a1a1a',background:'#fff' }}>
          <div style={{ display:'flex',justifyContent:'space-between',marginBottom:32 }}>
            <div>
              <div style={{ fontSize:26,fontWeight:800,color:'#1e3a5f',letterSpacing:'-0.5px' }}>TAX INVOICE</div>
              <div style={{ fontSize:13,color:'#6b7280',marginTop:4 }}>Invoice No: <strong style={{ color:'#111' }}>{invoice.InvoiceNo}</strong></div>
              <div style={{ fontSize:13,color:'#6b7280' }}>Date: <strong style={{ color:'#111' }}>{invoice.InvoiceDate ? new Date(invoice.InvoiceDate).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—'}</strong></div>
              {invoice.DueDate && <div style={{ fontSize:13,color:'#6b7280' }}>Due Date: <strong style={{ color:'#111' }}>{new Date(invoice.DueDate).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</strong></div>}
              {invoice.ReferenceNo && <div style={{ fontSize:13,color:'#6b7280' }}>Reference: <strong style={{ color:'#111' }}>{invoice.ReferenceNo}</strong></div>}
            </div>
            <div style={{ textAlign:'right' }}>
              <div style={{ fontSize:18,fontWeight:700,color:'#1e3a5f' }}>LTMS</div>
              <div style={{ fontSize:12,color:'#6b7280',marginTop:4 }}>Logistics &amp; Transport</div>
              <div style={{ fontSize:12,color:'#6b7280' }}>Management System</div>
            </div>
          </div>
          <div style={{ background:'#f8fafc',border:'1px solid #e2e8f0',borderRadius:8,padding:'14px 18px',marginBottom:24 }}>
            <div style={{ fontSize:11,fontWeight:700,color:'#6b7280',letterSpacing:'0.08em',marginBottom:6 }}>BILL TO</div>
            <div style={{ fontSize:15,fontWeight:700,color:'#111' }}>{invoice.customername || '—'}</div>
            {invoice.customercode && <div style={{ fontSize:12,color:'#6b7280',fontFamily:'monospace' }}>{invoice.customercode}</div>}
          </div>
          <table style={{ width:'100%',borderCollapse:'collapse',marginBottom:24 }}>
            <thead>
              <tr style={{ background:'#1e3a5f' }}>
                {['#','Consignment No','Description','Qty','Amount','Tax','Total'].map((h,i) => (
                  <th key={h} style={{ padding:'10px 12px',color:'#fff',fontSize:12,fontWeight:700,textAlign:i>=4?'right':'left',letterSpacing:'0.05em',borderRight:i<6?'1px solid rgba(255,255,255,0.15)':'none' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((row, idx) => {
                const amt   = parseFloat(row.cpd_Amount || row.UnitPrice || 0);
                const tax   = parseFloat(row.TaxAmt || 0);
                const total = parseFloat(row.TotalAmt || row.Amount || 0);
                return (
                  <tr key={row.InvoiceItemID || idx} style={{ background:idx%2===0?'#fff':'#f8fafc',borderBottom:'1px solid #e2e8f0' }}>
                    <td style={{ padding:'10px 12px',color:'#9ca3af',fontSize:12 }}>{idx+1}</td>
                    <td style={{ padding:'10px 12px',fontFamily:'monospace',fontWeight:600,color:'#2563eb',fontSize:12 }}>{row.ConsignmentNo||'—'}</td>
                    <td style={{ padding:'10px 12px',fontSize:12 }}>
                      <div style={{ fontWeight:600 }}>{row.revenuetypename||'—'}</div>
                      {row.PaymentMode && <div style={{ color:'#6b7280',fontSize:11 }}>{row.PaymentMode}</div>}
                      {row.ConsignmentDate && <div style={{ color:'#9ca3af',fontSize:11 }}>{new Date(row.ConsignmentDate).toLocaleDateString('en-IN')}</div>}
                    </td>
                    <td style={{ padding:'10px 12px',textAlign:'right',fontSize:12 }}>1</td>
                    <td style={{ padding:'10px 12px',textAlign:'right',fontFamily:'monospace',fontSize:12 }}>{amt.toFixed(2)}</td>
                    <td style={{ padding:'10px 12px',textAlign:'right',fontFamily:'monospace',fontSize:12 }}>{tax.toFixed(2)}</td>
                    <td style={{ padding:'10px 12px',textAlign:'right',fontFamily:'monospace',fontWeight:700,fontSize:12 }}>{total.toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ display:'flex',justifyContent:'flex-end',marginBottom:32 }}>
            <div style={{ minWidth:280 }}>
              {[{label:'Sub Total',value:subTotal},{label:'Tax',value:taxTotal}].map(r => (
                <div key={r.label} style={{ display:'flex',justifyContent:'space-between',padding:'6px 0',borderBottom:'1px solid #e2e8f0' }}>
                  <span style={{ color:'#6b7280',fontSize:13 }}>{r.label}</span>
                  <span style={{ fontFamily:'monospace',fontSize:13 }}>{r.value.toFixed(2)}</span>
                </div>
              ))}
              <div style={{ display:'flex',justifyContent:'space-between',padding:'10px 0 6px',borderTop:'2px solid #1e3a5f',marginTop:4 }}>
                <span style={{ fontWeight:800,fontSize:15,color:'#1e3a5f' }}>TOTAL AMOUNT</span>
                <span style={{ fontFamily:'monospace',fontWeight:800,fontSize:15,color:'#1e3a5f' }}>{grandTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>
          {(invoice.Notes || invoice.TermsConditions) && (
            <div style={{ borderTop:'1px solid #e2e8f0',paddingTop:16,display:'flex',gap:24 }}>
              {invoice.Notes && <div style={{ flex:1 }}><div style={{ fontSize:11,fontWeight:700,color:'#6b7280',letterSpacing:'0.08em',marginBottom:6 }}>NOTES</div><div style={{ fontSize:12,color:'#374151',lineHeight:1.6 }}>{invoice.Notes}</div></div>}
              {invoice.TermsConditions && <div style={{ flex:1 }}><div style={{ fontSize:11,fontWeight:700,color:'#6b7280',letterSpacing:'0.08em',marginBottom:6 }}>TERMS &amp; CONDITIONS</div><div style={{ fontSize:12,color:'#374151',lineHeight:1.6 }}>{invoice.TermsConditions}</div></div>}
            </div>
          )}
          <div style={{ marginTop:40,borderTop:'2px solid #e2e8f0',paddingTop:16,display:'flex',justifyContent:'space-between',alignItems:'flex-end' }}>
            <div style={{ fontSize:11,color:'#9ca3af' }}>Generated by LTMS · {new Date().toLocaleString('en-IN')}</div>
            <div style={{ textAlign:'right' }}>
              <div style={{ fontSize:11,color:'#6b7280',marginBottom:32 }}>Authorised Signatory</div>
              <div style={{ borderTop:'1px solid #374151',paddingTop:4,fontSize:11,color:'#6b7280',minWidth:160 }}>Signature</div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

/* ══════════════════════════════════════════════════════════════════════ */

const CustomerInvoiceForm = ({ editData = null, onClose, onSaved }) => {
  const [loading,     setLoading]     = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error,       setError]       = useState('');
  const [success,     setSuccess]     = useState('');
  const [showPrint,   setShowPrint]   = useState(false);

  const [allPending,       setAllPending]       = useState([]);
  const [editItems,        setEditItems]        = useState([]);
  const [editCustomerName, setEditCustomerName] = useState('');
  const [editCustomerID,   setEditCustomerID]   = useState('');

  const [selectedCustomerID, setSelectedCustomerID] = useState('');
  const [paymentSearch,      setPaymentSearch]      = useState('');
  const [selected,           setSelected]           = useState([]);
  const [showAddMore,        setShowAddMore]        = useState(false);
  const [addMoreSearch,      setAddMoreSearch]      = useState('');

  const [form, setForm] = useState({
    InvoiceNo: '', InvoiceDate: new Date().toISOString().slice(0,10),
    DueDate: '', PaymentTerms: 'net30', ReferenceNo: '', Notes: '',
    TermsConditions: '', Status: 'Draft',
  });

  const paymentTermOptions = [
    { id:'immediate',label:'Immediate' },{ id:'net15',label:'Net 15 Days' },
    { id:'net30',label:'Net 30 Days' },{ id:'net45',label:'Net 45 Days' },{ id:'net60',label:'Net 60 Days' },
  ];

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape' && !showPrint) onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose, showPrint]);

  useEffect(() => { loadInitialData(); }, [editData]); // eslint-disable-line

  const loadInitialData = async () => {
    setLoadingData(true); setError('');
    try {
      if (editData) {
        const [fullRes, pendingRes] = await Promise.all([
          apiClient.get(`/CustomerInvoices.php?id=${editData.InvoiceID}`),
          apiClient.get('/CustomerInvoices.php?uninvoiced-payments=1'),
        ]);
        if (pendingRes.data.success) setAllPending(pendingRes.data.data || []);
        if (fullRes.data.success) {
          const full = fullRes.data.data;
          setForm({ InvoiceNo: full.InvoiceNo||'', InvoiceDate:(full.InvoiceDate||'').slice(0,10), DueDate:(full.DueDate||'').slice(0,10), PaymentTerms:full.PaymentTerms||'net30', ReferenceNo:full.ReferenceNo||'', Notes:full.Notes||'', TermsConditions:full.TermsConditions||'', Status:full.Status||'Draft' });
          setEditCustomerID(full.CustomerID ? String(full.CustomerID) : '');
          setEditCustomerName(full.customername || '');
          const rawItems = full.Items || [];
          const seen = new Set();
          setEditItems(rawItems.filter(item => { const key = item.ConsignmentPaymentID||item.InvoiceItemID; if (seen.has(key)) return false; seen.add(key); return true; }));
        }
      } else {
        const [pendingRes, invNoRes] = await Promise.all([
          apiClient.get('/CustomerInvoices.php?uninvoiced-payments=1'),
          apiClient.get('/CustomerInvoices.php/next-number'),
        ]);
        if (pendingRes.data.success) setAllPending(pendingRes.data.data || []);
        if (invNoRes?.data?.success) setForm(p => ({ ...p, InvoiceNo: invNoRes.data.data.invoiceNo }));
      }
    } catch (err) { setError('Failed to load: ' + (err.response?.data?.message || err.message)); }
    finally { setLoadingData(false); }
  };

  const NO_CUSTOMER_KEY = '__NO_CUSTOMER__';

  const customerOptions = useMemo(() => {
    const map = {};
    allPending.forEach(r => {
      const id = r.CustomerID ? String(r.CustomerID) : NO_CUSTOMER_KEY;
      const name = r.customername || '(No customer assigned)';
      if (!map[id]) map[id] = { CustomerID:id, customername:name, count:0, total:0 };
      map[id].count++; map[id].total += parseFloat(r.TotalAmt||0);
    });
    return Object.values(map).sort((a,b) => { if (a.CustomerID===NO_CUSTOMER_KEY) return 1; if (b.CustomerID===NO_CUSTOMER_KEY) return -1; return a.customername.localeCompare(b.customername); });
  }, [allPending]);

  const selectedCustomer = useMemo(() => customerOptions.find(c => c.CustomerID === selectedCustomerID) || null, [customerOptions, selectedCustomerID]);
  const displayCustomerName = editData ? editCustomerName : (selectedCustomer?.customername || '');

  const customerPayments = useMemo(() => {
    if (editData || !selectedCustomerID) return [];
    if (selectedCustomerID === NO_CUSTOMER_KEY) return allPending.filter(r => !r.CustomerID);
    return allPending.filter(r => String(r.CustomerID) === selectedCustomerID);
  }, [allPending, selectedCustomerID, editData]);

  const visiblePayments = useMemo(() => {
    if (!paymentSearch.trim()) return customerPayments;
    const s = paymentSearch.toLowerCase();
    return customerPayments.filter(r => (r.ConsignmentNo||'').toLowerCase().includes(s)||(r.revenuetypename||'').toLowerCase().includes(s)||(r.PaymentMode||'').toLowerCase().includes(s));
  }, [customerPayments, paymentSearch]);

  const addMoreRows = useMemo(() => {
    if (!editData || !editCustomerID) return [];
    const existingIDs = new Set(editItems.map(i => i.ConsignmentPaymentID).filter(Boolean));
    const rows = editCustomerID === NO_CUSTOMER_KEY ? allPending.filter(r => !r.CustomerID) : allPending.filter(r => String(r.CustomerID) === editCustomerID);
    return rows.filter(r => !existingIDs.has(r.ConsignmentPaymentID));
  }, [allPending, editData, editCustomerID, editItems]);

  const visibleAddMore = useMemo(() => {
    if (!addMoreSearch.trim()) return addMoreRows;
    const s = addMoreSearch.toLowerCase();
    return addMoreRows.filter(r => (r.ConsignmentNo||'').toLowerCase().includes(s)||(r.revenuetypename||'').toLowerCase().includes(s)||(r.PaymentMode||'').toLowerCase().includes(s));
  }, [addMoreRows, addMoreSearch]);

  const selectedRows = useMemo(() => allPending.filter(r => selected.includes(r.ConsignmentPaymentID)), [allPending, selected]);

  const totals = useMemo(() => {
    const sub = editData ? editItems.reduce((s,r)=>s+parseFloat(r.cpd_Amount||r.UnitPrice||0),0) : selectedRows.reduce((s,r)=>s+parseFloat(r.Amount||0),0);
    const tax = editData ? editItems.reduce((s,r)=>s+parseFloat(r.TaxAmt||0),0) : selectedRows.reduce((s,r)=>s+parseFloat(r.TaxAmt||0),0);
    return { subTotal:sub, taxAmount:tax, totalAmount:sub+tax };
  }, [editData, editItems, selectedRows]);

  const handleCustomerChange = (e) => { setSelectedCustomerID(e.target.value); setSelected([]); setPaymentSearch(''); setError(''); };
  const toggleRow = (id) => setSelected(p => p.includes(id) ? p.filter(x=>x!==id) : [...p,id]);
  const allVisibleSelected = visiblePayments.length > 0 && visiblePayments.every(r => selected.includes(r.ConsignmentPaymentID));
  const toggleAll = () => { const ids = visiblePayments.map(r=>r.ConsignmentPaymentID); if (allVisibleSelected) setSelected(p=>p.filter(id=>!ids.includes(id))); else setSelected(p=>[...new Set([...p,...ids])]); };

  const handleRemoveEditItem = (invoiceItemID, idx) => {
    if (!window.confirm('Remove this row from the invoice?')) return;
    setEditItems(prev => prev.filter((_,i)=>i!==idx));
  };

  const buildDesc = (r) => { let d = r.ConsignmentNo||''; if (r.revenuetypename) d+=` \u2013 ${r.revenuetypename}`; if (r.PaymentMode) d+=` (${r.PaymentMode})`; return d; };

  const handleAddMoreRow = (pendingRow) => {
    const newItem = { ConsignmentPaymentID:pendingRow.ConsignmentPaymentID, ConsignmentID:pendingRow.ConsignmentID, ConsignmentNo:pendingRow.ConsignmentNo, ConsignmentDate:pendingRow.ConsignmentDate, revenuetypeid:pendingRow.revenuetypeid||null, revenuetypename:pendingRow.revenuetypename||'', PaymentMode:pendingRow.PaymentMode||'', Description:buildDesc(pendingRow), Quantity:1, UnitPrice:parseFloat(pendingRow.Amount||0), Amount:parseFloat(pendingRow.TotalAmt||0), cpd_Amount:parseFloat(pendingRow.Amount||0), TaxPer:parseFloat(pendingRow.TaxPer||0), TaxAmt:parseFloat(pendingRow.TaxAmt||0), TotalAmt:parseFloat(pendingRow.TotalAmt||0), Remarks:pendingRow.Remarks||'', _isNew:true };
    setEditItems(prev => { if (prev.some(p=>p.ConsignmentPaymentID===pendingRow.ConsignmentPaymentID)) return prev; return [...prev,newItem]; });
  };

  const handleFormChange = (e) => { const{name,value}=e.target; setForm(p=>({...p,[name]:value})); };

  const handleSave = async () => {
    if (!editData && !selectedCustomerID) { setError('Please select a customer'); return; }
    if (!editData && !selected.length)    { setError('Please select at least one payment row'); return; }
    if (editData && editItems.length===0) { setError('Invoice must have at least one row'); return; }
    const realCustomerID = editData ? (editCustomerID===NO_CUSTOMER_KEY?null:editCustomerID||null) : (selectedCustomerID===NO_CUSTOMER_KEY?null:selectedCustomerID||null);
    setLoading(true); setError(''); setSuccess('');
    try {
      const items = editData
        ? editItems.map((r,i)=>({ LineNumber:i+1, ConsignmentID:r.ConsignmentID||null, ConsignmentPaymentID:r.ConsignmentPaymentID||null, revenuetypeid:r.revenuetypeid||null, Description:r.Description||'', Quantity:r.Quantity||1, UnitPrice:parseFloat(r.cpd_Amount||r.UnitPrice||0), TaxPercent:parseFloat(r.TaxPer||0), DiscountPercent:0, Amount:parseFloat(r.TotalAmt||r.Amount||r.UnitPrice||0) }))
        : selectedRows.map((r,i)=>({ LineNumber:i+1, ConsignmentID:r.ConsignmentID, ConsignmentPaymentID:r.ConsignmentPaymentID, revenuetypeid:r.revenuetypeid||null, Description:buildDesc(r), Quantity:1, UnitPrice:parseFloat(r.Amount||0), TaxPercent:parseFloat(r.TaxPer||0), DiscountPercent:0, Amount:parseFloat(r.TotalAmt||0) }));
      const payload = { InvoiceNo:form.InvoiceNo, InvoiceDate:form.InvoiceDate, DueDate:form.DueDate||null, CustomerID:realCustomerID, CurrencyID:null, ExchangeRate:1, PaymentTerms:form.PaymentTerms, ReferenceNo:form.ReferenceNo||null, Notes:form.Notes||null, TermsConditions:form.TermsConditions||null, SubTotal:totals.subTotal, TaxPercent:0, TaxAmount:totals.taxAmount, DiscountPercent:0, DiscountAmount:0, TotalAmount:totals.totalAmount, BalanceAmount:totals.totalAmount, Status:form.Status, Items:items, ...(editData?{InvoiceID:editData.InvoiceID}:{}) };
      const res = editData ? await apiClient.put('/CustomerInvoices.php',payload) : await apiClient.post('/CustomerInvoices.php',payload);
      if (res.data.success) { setSuccess(`Invoice ${res.data.data?.InvoiceNo||form.InvoiceNo} ${editData?'updated':'created'} successfully!`); setTimeout(()=>onSaved(),900); }
      else throw new Error(res.data.message);
    } catch (err) { setError(err.response?.data?.message||err.message); }
    finally { setLoading(false); }
  };

  const rowCount = editData ? editItems.length : selected.length;
  const printInvoiceData = { InvoiceNo:form.InvoiceNo, InvoiceDate:form.InvoiceDate, DueDate:form.DueDate, ReferenceNo:form.ReferenceNo, Notes:form.Notes, TermsConditions:form.TermsConditions, customername:displayCustomerName, customercode:editData?.customercode||'' };

  // shared class helpers
  const inpCls  = 'w-full px-3 py-2 border border-gray-200 rounded-md text-sm text-gray-800 bg-white font-[inherit] outline-none transition-all duration-150 focus:border-violet-400 focus:shadow-[0_0_0_3px_rgba(102,126,234,0.1)] box-border';
  const lblCls  = 'block text-[12.5px] font-semibold text-gray-500 uppercase tracking-wide mb-1';
  const spinCls = { width:14,height:14,border:'2px solid rgba(255,255,255,0.4)',borderTopColor:'#fff',borderRadius:'50%',display:'inline-block',animation:'ci-spin 0.7s linear infinite' };

  const TbBtn = ({ onClick, disabled, children, save }) => (
    <button onClick={onClick} disabled={disabled}
      className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-all duration-150 border disabled:opacity-50 disabled:cursor-not-allowed ${save ? 'text-white border-violet-600' : 'bg-white text-gray-800 border-gray-200 hover:border-violet-400 hover:text-violet-600 hover:bg-violet-50'}`}
      style={save ? { background:`linear-gradient(135deg,${VIOLET},${VIOLET_DK})` } : {}}>
      {children}
    </button>
  );

  const SectionTitle = ({ icon: Icon, children }) => (
    <div className="flex items-center gap-2 text-[15px] font-semibold text-gray-800 mb-4 pb-3 border-b-2 border-[#e9e4f8]" style={{ color:'var(--ci-text)' }}>
      {Icon && <Icon size={15} style={{ color: VIOLET }} />}{children}
    </div>
  );

  const ConsBadge = ({ no }) => (
    <span className="font-semibold font-mono text-[13px]" style={{ color: VIOLET_MID }}>{no}</span>
  );

  return (
    <>
      <style>{`@keyframes ci-spin{to{transform:rotate(360deg);}}.ci-row-sel:hover td{background:#f5f3ff!important;}.ci-row-selected td{background:#ede9fe!important;}.ci-row-selected:hover td{background:#e9e2ff!important;}`}</style>

      {showPrint && <InvoicePrint invoice={printInvoiceData} items={editData ? editItems : selectedRows} onClose={() => setShowPrint(false)} />}

      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div className="bg-[#f5f5f8] rounded-xl w-full max-w-[1320px] max-h-[95vh] flex flex-col shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}>

          {/* Header */}
          <div className="flex items-center justify-between px-8 py-6 flex-shrink-0 border-b-[3px] border-white/15"
            style={{ background:`linear-gradient(135deg,${VIOLET} 0%,${VIOLET_DK} 100%)` }}>
            <div className="flex items-center gap-5 flex-1">
              <div className="w-14 h-14 rounded-[14px] bg-white/25 flex items-center justify-center flex-shrink-0 shadow-lg">
                <FileText size={26} color="#fff" />
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <span className="text-[1.6rem] font-bold text-white" style={{ textShadow:'0 2px 4px rgba(0,0,0,0.25)' }}>
                  {editData ? 'Edit Customer Invoice' : 'New Customer Invoice'}
                </span>
                <span className="text-white text-[0.95rem] font-semibold bg-white/20 px-3.5 py-1 rounded-md border border-white/35 font-mono">📋 {form.InvoiceNo || '—'}</span>
                {displayCustomerName && (
                  <span className="text-white text-[0.95rem] font-semibold px-3.5 py-1 rounded-md border border-white/30" style={{ background:'rgba(56,161,105,0.85)' }}>{displayCustomerName}</span>
                )}
                {rowCount > 0 && (
                  <span className="text-white text-[0.82rem] font-semibold bg-emerald-600/80 px-3 py-1 rounded-md border border-white/30">
                    {rowCount} row{rowCount!==1?'s':''} · {totals.totalAmount.toFixed(2)}
                  </span>
                )}
              </div>
            </div>
            <button onClick={onClose} title="Close (ESC)"
              className="w-11 h-11 flex items-center justify-center rounded-xl border cursor-pointer flex-shrink-0 transition-all duration-150 bg-white/18 border-white/30 hover:bg-white/30">
              <X size={20} color="#fff" />
            </button>
          </div>

          {/* Toolbar */}
          <div className="flex gap-2.5 flex-wrap px-6 py-3.5 bg-white border-b border-gray-200 flex-shrink-0">
            <TbBtn save onClick={handleSave} disabled={loading || (!editData && !selected.length) || loadingData}>
              {loading ? <><span style={spinCls} /> Saving…</> : <><Save size={16} /> {editData ? 'Update Invoice' : 'Save Invoice'}</>}
            </TbBtn>
            {rowCount > 0 && <TbBtn onClick={() => setShowPrint(true)} disabled={loading}><Printer size={15} /> Print Invoice</TbBtn>}
            {!editData && <TbBtn onClick={() => { setSelected([]); setPaymentSearch(''); }} disabled={loading}><RotateCcw size={16} /> Clear</TbBtn>}
            {!editData && selected.length > 0 && (
              <span className="text-sm text-gray-500 ml-2 self-center">{selected.length} selected → <strong style={{ color: VIOLET_MID }}>{totals.totalAmount.toFixed(2)}</strong></span>
            )}
          </div>

          {/* Alerts */}
          {error   && <div className="flex items-center gap-2 px-6 py-3 bg-red-50 border-b border-red-200 text-red-700 text-[13.5px] flex-shrink-0"><AlertCircle size={14} className="flex-shrink-0" /> {error}</div>}
          {success && <div className="px-6 py-3 bg-green-50 border-b border-green-300 text-green-800 text-[13.5px] flex-shrink-0">✓ {success}</div>}

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6 bg-[#f5f5f8] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-violet-300 [&::-webkit-scrollbar-thumb]:rounded">
            {loadingData ? (
              <div className="flex flex-col items-center gap-3 py-20 text-gray-500">
                <span style={{ width:36,height:36,border:'3px solid #e9e4f8',borderTopColor:VIOLET,borderRadius:'50%',animation:'ci-spin 0.8s linear infinite',display:'inline-block' }} />
                <span>Loading…</span>
              </div>
            ) : (<>

              {/* Top: form + summary */}
              <div className="grid grid-cols-[1fr_320px] gap-4 mb-4 max-[1024px]:grid-cols-1">
                {/* Left */}
                <div className="bg-white rounded-lg p-5 border border-gray-200 shadow-sm">
                  {/* Customer */}
                  <div className="mb-4">
                    <label className={lblCls}>
                      <span className="flex items-center gap-1.5"><User size={13} style={{ color: VIOLET }} /> Customer
                        {!editData && <span className="normal-case tracking-normal text-[11.5px] font-normal text-gray-400 ml-1">{customerOptions.length} with pending · {allPending.length} rows</span>}
                      </span>
                    </label>
                    {editData ? (
                      <div className="flex items-center gap-3 px-3.5 py-2.5 bg-emerald-50 border-2 border-emerald-300 rounded-lg text-emerald-800 text-[0.95rem]">
                        <User size={16} className="text-emerald-600 flex-shrink-0" />
                        <div><div className="font-bold">{displayCustomerName || '—'}</div><div className="text-[0.75rem] text-gray-500 mt-0.5">Customer locked when editing</div></div>
                      </div>
                    ) : (
                      <select className={`${inpCls} border-2 border-violet-400 shadow-[0_0_0_3px_rgba(102,126,234,0.08)] text-[14.5px] font-medium cursor-pointer focus:border-violet-600`}
                        value={selectedCustomerID} onChange={handleCustomerChange}>
                        <option value="">— Select a customer —</option>
                        {customerOptions.map(c => <option key={c.CustomerID} value={c.CustomerID}>{c.customername} ({c.count} pending · {c.total.toFixed(2)})</option>)}
                      </select>
                    )}
                  </div>

                  <SectionTitle icon={FileText}>Invoice Details</SectionTitle>
                  <div className="grid grid-cols-2 gap-4 max-[640px]:grid-cols-1">
                    <div><label className={lblCls}>Invoice No</label><input type="text" value={form.InvoiceNo} readOnly className={`${inpCls} bg-blue-50 font-bold text-blue-900 cursor-not-allowed`} /></div>
                    <div><label className={lblCls}>Status</label>
                      <select name="Status" value={form.Status} onChange={handleFormChange} className={`${inpCls} cursor-pointer`}>
                        {['Draft','Sent','Paid','Partially Paid','Overdue','Cancelled'].map(s=><option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div><label className={lblCls}>Invoice Date *</label><input type="date" name="InvoiceDate" value={form.InvoiceDate} onChange={handleFormChange} className={inpCls} /></div>
                    <div><label className={lblCls}>Due Date</label><input type="date" name="DueDate" value={form.DueDate} onChange={handleFormChange} className={inpCls} /></div>
                    <div><label className={lblCls}>Payment Terms</label>
                      <select name="PaymentTerms" value={form.PaymentTerms} onChange={handleFormChange} className={`${inpCls} cursor-pointer`}>
                        {paymentTermOptions.map(pt=><option key={pt.id} value={pt.id}>{pt.label}</option>)}
                      </select>
                    </div>
                    <div><label className={lblCls}>Reference No</label><input type="text" name="ReferenceNo" value={form.ReferenceNo} onChange={handleFormChange} placeholder="PO#, Job#…" className={inpCls} /></div>
                    <div className="col-span-2 max-[640px]:col-span-1"><label className={lblCls}>Notes</label><textarea name="Notes" value={form.Notes} onChange={handleFormChange} rows={2} placeholder="Additional notes…" className={`${inpCls} resize-y min-h-[64px]`} /></div>
                    <div className="col-span-2 max-[640px]:col-span-1"><label className={lblCls}>Terms &amp; Conditions</label><textarea name="TermsConditions" value={form.TermsConditions} onChange={handleFormChange} rows={2} placeholder="Payment terms…" className={`${inpCls} resize-y min-h-[64px]`} /></div>
                  </div>
                </div>

                {/* Right: summary panel */}
                <div className="bg-white rounded-lg p-5 border border-gray-200 shadow-sm flex flex-col max-[1024px]:min-w-0">
                  <SectionTitle icon={TrendingUp}>Invoice Summary</SectionTitle>
                  <div className={`rounded-lg px-3.5 py-2.5 mb-3.5 border ${displayCustomerName ? 'bg-violet-50 border-violet-200' : 'bg-gray-50 border-gray-200'}`}>
                    {displayCustomerName ? (<>
                      <div className="text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-0.5">Customer</div>
                      <div className="text-sm font-semibold" style={{ color: VIOLET_MID }}>{displayCustomerName}</div>
                      <div className="text-[0.75rem] text-gray-400 mt-1">
                        {editData ? `${editItems.length} row${editItems.length!==1?'s':''} on invoice` : `${customerPayments.length} pending · ${customerPayments.reduce((s,r)=>s+parseFloat(r.TotalAmt||0),0).toFixed(2)}`}
                      </div>
                    </>) : (
                      <div className="text-[0.83rem] text-gray-400 text-center py-2">Select a customer to begin</div>
                    )}
                  </div>
                  {rowCount === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-gray-400 text-center py-6 text-[13px] leading-relaxed">
                      <CheckSquare size={40} className="mb-2.5 opacity-15" />
                      <p>{editData ? 'No rows on invoice' : 'Select payments below'}</p>
                    </div>
                  ) : (
                    <div className="bg-violet-50 border border-violet-200 border-l-4 rounded-lg p-4 mt-3.5" style={{ borderLeftColor: VIOLET }}>
                      {[['Rows', rowCount, false],['Sub Total', totals.subTotal.toFixed(2), true],['Tax', totals.taxAmount.toFixed(2), true]].map(([label,val,mono]) => (
                        <div key={label} className="flex justify-between items-center py-1.5 border-b border-violet-100 text-sm last:border-b-0">
                          <span className="text-gray-500 font-medium">{label}</span>
                          <span className={`font-semibold ${mono?'font-mono':''}`}>{val}</span>
                        </div>
                      ))}
                      <div className="flex justify-between items-center pt-2.5 border-t-2 mt-1 font-bold text-[16px]" style={{ borderTopColor: VIOLET }}>
                        <span className="text-gray-800 font-bold">Total Amount</span>
                        <span className="font-mono text-[18px]" style={{ color: VIOLET_MID }}>{totals.totalAmount.toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Edit mode / New mode tables */}
              {editData ? (
                <div className="bg-white rounded-lg p-5 border border-gray-200 shadow-sm mb-4">
                  <div className="flex items-center justify-between flex-wrap gap-3 mb-3.5">
                    <SectionTitle icon={CheckSquare}>
                      Invoice Lines
                      <span className="inline-block bg-violet-100 text-violet-700 text-[12px] font-semibold px-2.5 py-0.5 rounded-full ml-2">{editItems.length} row{editItems.length!==1?'s':''}</span>
                    </SectionTitle>
                    {addMoreRows.length > 0 && (
                      <TbBtn onClick={() => { setShowAddMore(v=>!v); setAddMoreSearch(''); }}>
                        <Plus size={14} /> {showAddMore ? 'Hide' : `Add More (${addMoreRows.length} available)`}
                      </TbBtn>
                    )}
                  </div>

                  {editItems.length === 0 ? (
                    <div className="text-center py-8 text-gray-400 text-sm">No rows — use "Add More" to add payments.</div>
                  ) : (
                    <div className="border border-gray-200 rounded-lg overflow-hidden max-h-[440px] overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-violet-300 [&::-webkit-scrollbar-thumb]:rounded">
                      <table className="w-full border-collapse text-[13.5px] bg-white">
                        <thead style={{ background:'linear-gradient(135deg,#f0ebff,#e8e0f8)' }}>
                          <tr>
                            {['#','Consignment No','Date','Item / Mode','Amount','Tax','Total',''].map(h => (
                              <th key={h} className={`px-3.5 py-3 border-b-2 border-[#d6c9f5] text-[12.5px] font-semibold uppercase tracking-wide whitespace-nowrap ${['Amount','Tax','Total'].includes(h)?'text-right':'text-left'}`}
                                style={{ color: VIOLET_MID }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {editItems.map((row, idx) => {
                            const amt    = parseFloat(row.cpd_Amount||row.UnitPrice||0);
                            const taxAmt = parseFloat(row.TaxAmt||0);
                            const total  = parseFloat(row.TotalAmt||row.Amount||row.UnitPrice||0);
                            const date   = row.ConsignmentDate ? new Date(row.ConsignmentDate).toLocaleDateString() : '—';
                            return (
                              <tr key={row.InvoiceItemID||`new-${idx}`} className={`border-b border-[#f0f0f8] ${row._isNew?'bg-violet-50':''}`}>
                                <td className="px-3.5 py-2.5 text-gray-400 text-[12px]">{idx+1}</td>
                                <td className="px-3.5 py-2.5"><ConsBadge no={row.ConsignmentNo||'—'} /></td>
                                <td className="px-3.5 py-2.5 text-[12px] text-gray-500 whitespace-nowrap">{date}</td>
                                <td className="px-3.5 py-2.5 text-[12px]">
                                  {row.revenuetypename && <div className="font-semibold">{row.revenuetypename}</div>}
                                  {row.PaymentMode && <div className="text-gray-500">{row.PaymentMode}</div>}
                                </td>
                                <td className="px-3.5 py-2.5 text-right font-mono">{amt.toFixed(2)}</td>
                                <td className="px-3.5 py-2.5 text-right font-mono">{taxAmt.toFixed(2)}</td>
                                <td className="px-3.5 py-2.5 text-right font-mono font-bold">{total.toFixed(2)}</td>
                                <td className="px-3.5 py-2.5 text-center">
                                  <button type="button" onClick={() => handleRemoveEditItem(row.InvoiceItemID, idx)}
                                    className="bg-transparent border-0 cursor-pointer text-red-500 p-1 rounded hover:bg-red-50 transition-colors duration-100" title="Remove from invoice">
                                    <Trash2 size={14} />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr className="bg-violet-50 border-t-2 border-violet-300">
                            <td colSpan={4} className="px-3.5 py-2.5 text-right font-semibold text-gray-500 text-[13px]">{editItems.length} row{editItems.length!==1?'s':''} — total:</td>
                            <td className="px-3.5 py-2.5 text-right font-mono font-semibold">{editItems.reduce((s,r)=>s+parseFloat(r.cpd_Amount||r.UnitPrice||0),0).toFixed(2)}</td>
                            <td className="px-3.5 py-2.5 text-right font-mono font-semibold">{editItems.reduce((s,r)=>s+parseFloat(r.TaxAmt||0),0).toFixed(2)}</td>
                            <td className="px-3.5 py-2.5 text-right font-mono font-bold" style={{ color: VIOLET_MID }}>{editItems.reduce((s,r)=>s+parseFloat(r.TotalAmt||r.Amount||r.UnitPrice||0),0).toFixed(2)}</td>
                            <td />
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}

                  {/* Add more panel */}
                  {showAddMore && addMoreRows.length > 0 && (
                    <div className="mt-4 p-3.5 bg-blue-50 border-2 border-dashed border-sky-400 rounded-lg">
                      <div className="flex items-center gap-2.5 mb-2.5">
                        <span className="font-bold text-[0.87rem] text-sky-700"><Plus size={14} className="inline" /> Add uninvoiced payments ({addMoreRows.length})</span>
                        <div className="relative flex-1 min-w-[240px]">
                          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                          <input className={`${inpCls} pl-8`} type="text" placeholder="Search…" value={addMoreSearch} onChange={e=>setAddMoreSearch(e.target.value)} />
                          {addMoreSearch && <button className="absolute right-2 top-1/2 -translate-y-1/2 bg-transparent border-0 cursor-pointer text-gray-400" onClick={()=>setAddMoreSearch('')}><X size={11} /></button>}
                        </div>
                      </div>
                      <table className="w-full border-collapse text-[0.83rem] bg-white rounded-lg overflow-hidden border border-gray-200">
                        <thead style={{ background:'linear-gradient(135deg,#f0ebff,#e8e0f8)' }}>
                          <tr>{['Consignment No','Date','Item','Payment Mode','Amount','Tax','Total',''].map(h=><th key={h} className={`px-3 py-2.5 border-b-2 border-[#d6c9f5] text-[12px] font-semibold uppercase tracking-wide text-left whitespace-nowrap`} style={{color:VIOLET_MID}}>{h}</th>)}</tr>
                        </thead>
                        <tbody>
                          {visibleAddMore.map(row => (
                            <tr key={row.ConsignmentPaymentID} className="border-b border-gray-100 hover:bg-violet-50 transition-colors duration-75">
                              <td className="px-3 py-2"><ConsBadge no={row.ConsignmentNo} /></td>
                              <td className="px-3 py-2 text-[12px] text-gray-500">{row.ConsignmentDate?new Date(row.ConsignmentDate).toLocaleDateString():'—'}</td>
                              <td className="px-3 py-2">{row.revenuetypename||'—'}</td>
                              <td className="px-3 py-2">{row.PaymentMode||'—'}</td>
                              <td className="px-3 py-2 text-right font-mono">{parseFloat(row.Amount||0).toFixed(2)}</td>
                              <td className="px-3 py-2 text-right font-mono">{parseFloat(row.TaxAmt||0).toFixed(2)}</td>
                              <td className="px-3 py-2 text-right font-mono font-bold">{parseFloat(row.TotalAmt||0).toFixed(2)}</td>
                              <td className="px-3 py-2 text-center">
                                <button type="button" onClick={() => handleAddMoreRow(row)}
                                  className="bg-sky-700 text-white border-0 cursor-pointer px-2.5 py-1 rounded text-[0.78rem] font-semibold hover:bg-sky-800 transition-colors duration-100">+ Add</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

              ) : selectedCustomerID ? (
                /* New mode — pending payments */
                <div className="bg-white rounded-lg p-5 border border-gray-200 shadow-sm mb-4">
                  <div className="flex items-center justify-between flex-wrap gap-3 mb-3.5">
                    <div className="flex items-center gap-2 text-[15px] font-semibold text-gray-800">
                      <CheckSquare size={15} style={{ color: VIOLET }} />
                      Pending Payments — {displayCustomerName}
                      <span className="inline-block bg-violet-100 text-violet-700 text-[12px] font-semibold px-2.5 py-0.5 rounded-full ml-2">{customerPayments.length} pending</span>
                    </div>
                    <div className="relative min-w-[260px]">
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                      <input className={`${inpCls} pl-9 pr-8`} type="text" placeholder="Search consignment, item, mode…" value={paymentSearch} onChange={e=>setPaymentSearch(e.target.value)} />
                      {paymentSearch && <button className="absolute right-2.5 top-1/2 -translate-y-1/2 bg-transparent border-0 cursor-pointer text-gray-400" onClick={()=>setPaymentSearch('')}><X size={12} /></button>}
                    </div>
                  </div>
                  {customerPayments.length === 0 ? (
                    <div className="text-center py-10 text-gray-400"><CheckSquare size={40} className="mx-auto mb-2.5 opacity-15" /><h3 className="font-semibold text-gray-600 mb-1">No Pending Payments</h3><p className="text-sm">All payments for this customer have been invoiced.</p></div>
                  ) : visiblePayments.length === 0 ? (
                    <div className="text-center py-6 text-gray-400 text-sm">No results match your search.</div>
                  ) : (
                    <div className="border border-gray-200 rounded-lg overflow-hidden max-h-[440px] overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-violet-300 [&::-webkit-scrollbar-thumb]:rounded">
                      <table className="w-full border-collapse text-[13.5px] bg-white">
                        <thead style={{ background:'linear-gradient(135deg,#f0ebff,#e8e0f8)' }}>
                          <tr>
                            <th className="w-10 text-center px-3 py-3 border-b-2 border-[#d6c9f5]">
                              <input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} title="Select / deselect all" className="w-4 h-4 cursor-pointer accent-violet-500" />
                            </th>
                            {['Consignment No','Date','Item','Payment Mode','Amount','Tax','Total','Remarks'].map(h=>(
                              <th key={h} className={`px-3.5 py-3 border-b-2 border-[#d6c9f5] text-[12.5px] font-semibold uppercase tracking-wide whitespace-nowrap ${['Amount','Tax','Total'].includes(h)?'text-right':'text-left'}`} style={{color:VIOLET_MID}}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {visiblePayments.map(row => {
                            const isSel = selected.includes(row.ConsignmentPaymentID);
                            return (
                              <tr key={row.ConsignmentPaymentID}
                                className={`border-b border-[#f0f0f8] cursor-pointer ci-row-sel ${isSel?'ci-row-selected':''}`}
                                onClick={() => toggleRow(row.ConsignmentPaymentID)}>
                                <td className="px-3 py-2.5 text-center" onClick={e=>e.stopPropagation()}>
                                  <input type="checkbox" checked={isSel} onChange={()=>toggleRow(row.ConsignmentPaymentID)} className="w-4 h-4 cursor-pointer accent-violet-500" />
                                </td>
                                <td className="px-3.5 py-2.5"><ConsBadge no={row.ConsignmentNo} /></td>
                                <td className="px-3.5 py-2.5 whitespace-nowrap text-gray-500 text-[13px]">{row.ConsignmentDate?new Date(row.ConsignmentDate).toLocaleDateString():'—'}</td>
                                <td className="px-3.5 py-2.5 text-[13px]">{row.revenuetypename||'—'}</td>
                                <td className="px-3.5 py-2.5 text-[13px]">{row.PaymentMode||'—'}</td>
                                <td className="px-3.5 py-2.5 text-right font-mono">{parseFloat(row.Amount||0).toFixed(2)}</td>
                                <td className="px-3.5 py-2.5 text-right font-mono">{parseFloat(row.TaxAmt||0).toFixed(2)}</td>
                                <td className="px-3.5 py-2.5 text-right font-mono font-bold">{parseFloat(row.TotalAmt||0).toFixed(2)}</td>
                                <td className="px-3.5 py-2.5 text-[12px] text-gray-400 max-w-[130px] truncate">{row.Remarks||'—'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr className="bg-violet-50 border-t-2 border-violet-300">
                            <td colSpan={5} className="px-3.5 py-2.5 text-right font-semibold text-gray-500 text-[13px]">{visiblePayments.length} row{visiblePayments.length!==1?'s':''} — total:</td>
                            <td className="px-3.5 py-2.5 text-right font-mono font-semibold">{visiblePayments.reduce((s,r)=>s+parseFloat(r.Amount||0),0).toFixed(2)}</td>
                            <td className="px-3.5 py-2.5 text-right font-mono font-semibold">{visiblePayments.reduce((s,r)=>s+parseFloat(r.TaxAmt||0),0).toFixed(2)}</td>
                            <td className="px-3.5 py-2.5 text-right font-mono font-bold" style={{color:VIOLET_MID}}>{visiblePayments.reduce((s,r)=>s+parseFloat(r.TotalAmt||0),0).toFixed(2)}</td>
                            <td />
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-lg text-center py-12 px-6 mb-4">
                  <Package size={44} className="mx-auto mb-3 opacity-10" />
                  <h3 className="text-[1rem] text-gray-500 font-semibold m-0 mb-1.5">Select a customer above</h3>
                  <p className="text-[0.87rem] text-gray-400 m-0">Their uninvoiced consignment payments will appear here</p>
                </div>
              )}

            </>)}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2.5 px-6 py-3.5 bg-white border-t border-gray-200 flex-shrink-0">
            <span className="flex-1 text-[13px] text-gray-500 font-medium">
              {editData
                ? `${editItems.length} row${editItems.length!==1?'s':''} · Total: ${totals.totalAmount.toFixed(2)}`
                : !selectedCustomerID
                  ? `${customerOptions.length} customer${customerOptions.length!==1?'s':''} with pending · ${allPending.length} total rows`
                  : selected.length > 0 ? `${selected.length} selected · Total: ${totals.totalAmount.toFixed(2)}`
                  : `${customerPayments.length} payment${customerPayments.length!==1?'s':''} available`}
            </span>
            <TbBtn onClick={onClose} disabled={loading}>Cancel</TbBtn>
            <TbBtn save onClick={handleSave} disabled={loading || (!editData && !selected.length) || loadingData}>
              {loading ? <><span style={spinCls} /> Saving…</> : <><Save size={16} /> {editData ? 'Update Invoice' : 'Save Invoice'}</>}
            </TbBtn>
          </div>

        </div>
      </div>
    </>
  );
};

export default CustomerInvoiceForm;